/**
 * 出图参数弹窗 — 2026-05-25 自由创作改版。
 *
 * 把原本平铺成三张大卡的「画面比例 / 生成张数 / 分辨率」+「模型(高级)」收进文案框旁边
 * 的一个 ⚙ 参数 Popover。低技能用户默认不用打开(默认 1:1 · 1 张 · GPT · 自动分辨率),
 * 有需要的人点开微调。模型放「高级」分隔区,默认隐藏在视觉次级。
 *
 * 比例改动会 setRatioUserTouched(true) —— 标记「用户手选过」,让 AI 优化推荐的比例
 * 不再覆盖用户的显式选择(见 useFreeCreateActions 比例优先级)。
 */
import { usePriceBook } from "@/entities/pricing/model/usePriceBook";
import { useState } from "react";
import { Settings2, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/shared/ui/button";
import { Label } from "@/shared/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/ui/popover";
import { Separator } from "@/shared/ui/separator";
import { Switch } from "@/shared/ui/switch";
import { cn } from "@/shared/lib/utils";
import { useCompliance } from "@/contexts/AuthContext";

import { RESOLUTION_TIERS } from "../../../model/free-create/useFreeCreateConstraints";
import { CHANNEL_GROUPS } from "../../../model/free-create/channelPresets";
import { SKU, imageVariant, type PriceBook } from "@/entities/pricing/model/priceBook";
import { grouped } from "@/entities/scene-card/model/creditRates";
import type { Count, Ratio } from "../../../model/free-create/useFreeCreateForm";
import type { Provider, ResolutionTier } from "../../../api/posterService";
import type { GroupPointForm } from "../../../model/blueprint";
import {
  detectOutputMode,
  effectiveCount,
  showsSmartRatio,
  type OutputMode,
} from "../../../model/free-create/outputMode";

interface Props {
  // 比例
  supportedRatios: ReadonlyArray<{ value: Ratio; label: string; tag: string; desc: string }>;
  validRatio: Ratio;
  setRatio: (v: Ratio) => void;
  /** 用户是否手选过比例。false = 「智能」(交大模型/文案推断),true = 锁定某个比例。 */
  ratioUserTouched: boolean;
  setRatioUserTouched: (v: boolean) => void;

  /** 张数 —— 只用来算摘要里的真实出图数(控件已删,见下方注释)。 */
  validCount: Count;

  // 分辨率
  supportedTiers: ReadonlyArray<ResolutionTier>;
  validTier: ResolutionTier | null;
  setResolutionTier: (v: ResolutionTier | null) => void;
  isCombinationLegal: (ratio: Ratio, tier: ResolutionTier) => boolean;
  resolveSize?: (ratio: Ratio, tier: ResolutionTier) => [number, number] | null;

  // 高级:模型
  provider: Provider;
  setProvider: (v: Provider) => void;
  enabledProviders: ReadonlyArray<{ value: Provider; label: string; desc: string }>;
  useGptThinking: boolean;
  setUseGptThinking: (v: boolean) => void;

  /** P-B 多比例套图:非 null = 套图开(多选比例,每比例 1 张);仅 T2I 生效。 */
  ratioSet?: Ratio[] | null;
  setRatioSet?: (v: Ratio[] | null) => void;
  /**
   * 2026-07-28 W4 — 组图爆点(**只读**:本弹层据此判当前形态并给指引)。
   *
   * ★2026-08-01:原本还有一个 `setGroupPoints`,文档写着「传了就渲染『出什么』三选一」——
   *   而 7-31 创作页重构已经把**形态切换搬去卡墙 + 顶部形态条**了(见下方 modeEnabled 处
   *   那段注释),这里再没有任何地方调用它。留着的是一个**说自己能改、其实改不了**的契约:
   *   看接口的人会以为传了就能在弹层里切组图。连同过期文档一起删,爆点增删的唯一入口
   *   是「组图模式」条(`onPointsChange={form.setGroupPoints}`)。
   */
  groupPoints?: GroupPointForm[] | null;
  /** W6:主体一致性锚定(仅组图/套图有意义)。 */
  keepSubjectConsistent?: boolean;
  setKeepSubjectConsistent?: (v: boolean) => void;
  /** i2i 模式:比例语义是「跟随原图」,默认不强制 — 文案微调即可 */
  ratioFollowsSource?: boolean;
  /** 2026-07-13 — 参考图实测映射档(选图时量宽高就近 5 档);null=尚未选图/量图失败。 */
  sourceRatio?: Ratio | null;
  /**
   * 1005:组图态**实际派发**的画幅(FreeCreateBody 的 ratioRuling,与底栏/提交侧同源)。
   * 组图不走智能推断,但文案里写了「小红书」这类渠道词时提交侧按它出 3:4,
   * 摘要若仍写 validRatio(1:1)就是同一件事两个口径。未传退回 validRatio。
   */
  groupRatio?: Ratio | string | null;
  /**
   * 这一单只能 1K 时的原因(挂了多图参考 / GPT 系改图,见 multiRefImages.only1KReason)。
   * ★1020 起 2K/4K 直接不出(原来是置灰 + 黄字):摆两个点不动的档位只会让人以为是坏了。
   */
  only1KReason?: string | null;
}

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <div className="mb-1.5 flex items-center gap-1.5 text-[12.5px] font-medium text-muted-foreground">
    <Sparkles className="h-3 w-3" />
    {children}
  </div>
);

/**
 * 某一档画质一张图多少积分 —— 走**后端价目表**,不在组件里硬编数字,也不读前端常量。
 *
 * ★ 取不到价返空串:这个数印在「1K / 2K / 4K」三个档位按钮上,用户是照着它选档的。
 *   兜一个过期的数,他会按错的价差做决定(旧表 2K/4K 的差是 2 倍,现表是 1.33 倍)。
 */
/** GPT 线路上游有时不认尺寸(1005 生产:2K 单交 1086×1448)。 */
const OPENAI_SIZE_NOT_GUARANTEED_HINT =
  "该引擎上游有时只交约 1.6M 像素,达不到所选规格;成品页会标出实际像素";

function creditsForTier(book: PriceBook, provider: string, tier: ResolutionTier): string {
  const credits = book.unit(SKU.posterImage, imageVariant(provider, tier));
  return credits ? grouped(credits) : "";
}

export function ParamsPopover({
  supportedRatios,
  validRatio,
  setRatio,
  ratioUserTouched,
  setRatioUserTouched,
  validCount,
  supportedTiers,
  validTier,
  setResolutionTier,
  isCombinationLegal,
  resolveSize,
  provider,
  setProvider,
  enabledProviders,
  useGptThinking,
  setUseGptThinking,
  ratioSet = null,
  setRatioSet,
  groupPoints = null,
  keepSubjectConsistent = true,
  setKeepSubjectConsistent,
  ratioFollowsSource = false,
  only1KReason = null,
  sourceRatio = null,
  groupRatio = null,
}: Props) {
  // 档位积分来自后端价目表(这三个数是用户选档的唯一依据)
  const { book: priceBook } = usePriceBook();
  const { t } = useTranslation("poster");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const { domesticOnly } = useCompliance();

  // W4:模式是派生量(不新增 state,避免与 groupPoints / ratioSet 双写不同步)
  // ★2026-07-29:判据从「组图和套图两个 setter 都在」放宽成「套图 setter 在」——
  //   i2i 挂了原图时可以出套图但不能出组图(组图要换内容,跟「同一张换画幅」不是一回事),
  //   `setGroupPoints` 因此恒为 undefined。原判据会把整块「出什么」连同套图一起藏掉:
  //   从详情页直连过来 ratioSet 已经设好、会真出 3 张,而界面上看不到也改不了。
  const modeEnabled = !!setRatioSet && !ratioFollowsSource;
  const mode: OutputMode = modeEnabled ? detectOutputMode(groupPoints, ratioSet) : "MULTI";
  const realCount = effectiveCount(mode, { groupPoints, ratioSet, count: validCount });

  // 「智能」= 没手选过比例(交大模型/文案推断)。i2i 跟随原图时不算智能态。
  // W4:只在「多张同款」出现 —— 组图要固定比例做轮播、套图本身就在选比例。
  const showSmart = modeEnabled ? showsSmartRatio(mode, ratioFollowsSource) : !ratioFollowsSource;
  const smartActive = showSmart && !ratioUserTouched;

  // 摘要 chip 文案:让用户不打开也知道当前参数。
  // 跟随原图且已量出实测档 → 带档位(「跟随原图 · 3:4」),用户可见真实生效比例。
  const ratioSummary = ratioFollowsSource
    ? sourceRatio
      ? `${t("freeCreate.ratioFollowSource")} · ${sourceRatio}`
      : t("freeCreate.ratioFollowSource")
    : smartActive
      ? t("freeCreate.ratioAuto")
      : validRatio;
  const tierSuffix = validTier ? ` · ${validTier}` : "";
  const summary =
    mode === "RATIO_SET"
      ? `套图 ${(ratioSet ?? []).join("+")}${tierSuffix}`
      : mode === "GROUP"
        ? `组图 ${groupRatio ?? validRatio} · ${t("freeCreate.countUnit", { n: realCount })}${tierSuffix}`
        : `${ratioSummary} · ${t("freeCreate.countUnit", { n: realCount })}${tierSuffix}`;

  return (
    <Popover>
      <PopoverTrigger asChild>
        {/* 2026-05-25 — 醒目化:用户反映看不到参数/画面比例入口。放大 + 主色描边 +
            常显当前摘要(含画面比例),让它一眼是个可点的控件而不是装饰图标。 */}
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-9 gap-2 px-3.5 text-sm font-medium cursor-pointer border-primary/50 bg-primary/5 text-foreground shadow-sm hover:bg-primary/10"
          aria-label={t("freeCreate.paramsTitle")}
        >
          <Settings2 className="h-4 w-4 text-primary" />
          <span>{t("freeCreate.ratioAndParams")}</span>
          <span className="rounded bg-primary/10 px-1.5 py-0.5 text-[13px] font-normal text-primary">
            {summary}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 space-y-4 p-4">
        {/* 2026-07-30 —— 「出什么」产出模式单选整块删。
            ★它不是参数,是**能力切换**:三种产出的段内容完全不同(组图要爆点编辑器、
              套图要渠道多选、单图要比例单选),当参数摆在这里,页面内容会随它整块换掉 ——
              正是「三个互斥控件靠灰显打补丁」那批的病根。
            ★入口没少:卡墙三张卡是主入口,页面顶部形态条显示当前是哪一种,
              写了字之后那行 handoff 链接带着文案去另一张卡(见 PosterModeBar)。
            ★下面这些**跟着形态走**的项仍在,只是不再由这里切换形态:
              组图/套图 → 保持主体一致;套图 → 比例变多选。 */}
        {modeEnabled && (
          <div>
            {mode === "GROUP" && (
              <p className="text-[12.5px] text-muted-foreground">
                每个爆点一张(小红书轮播 / 详情组图)。爆点在
                <span className="font-medium text-foreground">上方「组图模式」条</span>
                里增删,<span className="font-medium text-foreground">张数跟着爆点数走</span>。
              </p>
            )}
            {mode === "RATIO_SET" && (
              <p className="text-[12.5px] text-muted-foreground">
                同一内容一次出多个画幅 ——{" "}
                <span className="font-medium text-foreground">勾几个比例就出几张</span>。
              </p>
            )}

            {/* W6 主体一致性 —— 用户实测「出来的两只小猫看着也不一样」。
                多张才有「一致」可言,单张模式下不出现。 */}
            {mode !== "MULTI" && setKeepSubjectConsistent && (
              <label className="mt-2 flex cursor-pointer items-start gap-2 rounded-md border border-border/60 bg-background/40 p-2">
                <Switch
                  checked={keepSubjectConsistent}
                  onCheckedChange={setKeepSubjectConsistent}
                  aria-label="保持主体一致"
                  className="mt-0.5"
                />
                <span className="min-w-0 text-[12.5px] leading-relaxed">
                  <span className="font-medium text-foreground">保持主体一致</span>
                  <span className="ml-1 text-muted-foreground">
                    {keepSubjectConsistent
                      ? "先出第 1 张,后面几张以它为准画 —— 主体不会变样,但要逐张生成(约慢 2~3 倍)"
                      : "每张各自发挥,快;主体可能长得不一样"}
                  </span>
                </span>
              </label>
            )}
          </div>
        )}

        {/* 画面比例 */}
        <div>
          <SectionLabel>{t("freeCreate.ratioTitle")}</SectionLabel>
          <div className="flex flex-wrap gap-1.5">
            {/* 「智能」比例:完全交大模型按文案推断,不锁死某个比例。放第一个 + 主色高亮。
                W4:组图/套图模式下不出现(那两种语境里它是噪音,还会让用户以为自己没选比例)。 */}
            {showSmart && (
              <button
                type="button"
                title={t("freeCreate.ratioAutoDesc")}
                onClick={() => setRatioUserTouched(false)}
                className={cn(
                  "inline-flex items-center gap-1 rounded-md border px-3 py-1.5 text-[14px] font-semibold transition-colors",
                  smartActive
                    ? "border-primary bg-primary/15 text-primary shadow-sm"
                    : "border-primary/40 bg-primary/5 text-primary/80 hover:bg-primary/10"
                )}
                aria-pressed={smartActive}
              >
                <Sparkles className="h-3.5 w-3.5" />
                <span>{t("freeCreate.ratioAuto")}</span>
              </button>
            )}
            {supportedRatios.map((r) => {
              // W4 套图态:比例是**多选**(每勾一个多出一张),不是单选
              const picked = mode === "RATIO_SET" && (ratioSet ?? []).includes(r.value);
              const active =
                mode === "RATIO_SET"
                  ? picked
                  : !ratioFollowsSource && ratioUserTouched && validRatio === r.value;
              return (
                <button
                  key={r.value}
                  type="button"
                  title={r.desc}
                  aria-checked={mode === "RATIO_SET" ? picked : undefined}
                  role={mode === "RATIO_SET" ? "checkbox" : undefined}
                  onClick={() => {
                    if (mode === "RATIO_SET") {
                      const cur = ratioSet ?? [];
                      const next = picked ? cur.filter((x) => x !== r.value) : [...cur, r.value];
                      // 至少留 1 个(全空会退回单比例,反而更迷惑)
                      setRatioSet?.(next.length > 0 ? next : cur);
                      return;
                    }
                    setRatio(r.value);
                    setRatioUserTouched(true);
                  }}
                  className={cn(
                    "inline-flex items-center gap-1 rounded-md border px-3 py-1.5 text-[14px] font-medium transition-colors",
                    active
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border/60 bg-background/40 text-muted-foreground hover:border-primary/40 hover:text-foreground"
                  )}
                  aria-pressed={active}
                >
                  <span className="font-mono">{r.tag}</span>
                  <span>{r.label}</span>
                </button>
              );
            })}
          </div>
          {smartActive && (
            <p className="mt-1.5 text-[12.5px] text-muted-foreground">
              {t("freeCreate.ratioAutoDesc")}
            </p>
          )}
          {ratioFollowsSource && (
            <p className="mt-1 text-2xs text-muted-foreground">
              {t("freeCreate.ratioFollowSourceHint")}
            </p>
          )}
          {mode === "RATIO_SET" && (
            <>
              {/* T4(2026-07-29):按**渠道**选,比例是实现细节。
                  用户心智是「我要发抖音 / 小红书 / 朋友圈」,不是「我要 9:16 / 3:4 / 1:1」——
                  卡面写着「全渠道套图」,进来却只让勾比例,名字和界面说的不是一回事。
                  上面那排比例 chip 保留为高级入口(存量口径不动),两边同源于 ratioSet。 */}
              <div className="mt-2 rounded-md border border-border/50 bg-background/40 p-2">
                <div className="mb-1.5 text-[12.5px] font-medium text-foreground">发到哪些渠道</div>
                <div className="flex flex-wrap gap-1.5">
                  {/* ★2026-07-30(任务 5):chip 的单位是**按比例合并后的渠道组**,不是单个渠道。
                      同比例的两个渠道(朋友圈/公众号 与 淘宝主图 都是 1:1)出的是同一张图,
                      拆成两个 chip 会共享同一个状态位 → 取消任一个都没反应。合并后
                      「组 ↔ 比例」1:1,取消就是取消,不再需要「还有谁在用这个比例」的判断。 */}
                  {CHANNEL_GROUPS.map((g) => {
                    const cur = ratioSet ?? [];
                    const on = cur.includes(g.ratio);
                    // 至少要留一个比例,否则套图无从生成。最后一个不许取消 —— 但要**看得出来**
                    // 是不可点(禁用态 + 悬浮说明),不是点了没反应。
                    const isLastOn = on && cur.length === 1;
                    return (
                      <button
                        key={g.ratio}
                        type="button"
                        role="checkbox"
                        aria-checked={on}
                        aria-disabled={isLastOn}
                        title={isLastOn ? `${g.title} —— 至少要留一个渠道` : g.title}
                        onClick={() => {
                          if (isLastOn) return;
                          setRatioSet?.(on ? cur.filter((x) => x !== g.ratio) : [...cur, g.ratio]);
                        }}
                        className={cn(
                          "inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[12.5px] transition-colors",
                          on
                            ? "border-primary bg-primary/10 text-primary"
                            : "border-border/60 bg-background/40 text-muted-foreground hover:border-primary/40",
                          isLastOn && "cursor-not-allowed opacity-60"
                        )}
                      >
                        {g.label}
                        <span className="font-mono text-2xs opacity-70">{g.ratio}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
              <p className="mt-1.5 text-[12.5px] text-muted-foreground">
                勾几个比例就出几张 ·{" "}
                <span className="font-medium text-foreground">{realCount}</span> 张
                {realCount < 2 && "(至少勾 2 个才算套图)"}
              </p>
            </>
          )}
        </div>

        {/* W4:老的「多比例套图」独立开关块已删 —— 它的职责被顶部「出什么」单选 +
            上面比例区的多选接管。老写法是「开关 + 一排重复的比例 chip」,同一个比例
            在弹层里出现两次(上面单选一次、这里多选一次),用户分不清点哪个才算数。 */}
        {/* 2026-07-30 —— 「出几张挑」控件删。
            ★不是砍能力,是**决策时机不对**:出图前用户没有信息回答「我要几张」。
              `regenerateAsset(taskId, assetId)` body 全空 = 同 prompt 不同 seed(详情页
              已有按钮),所以「预付 4 张」与「出图后点四次」积分一样、能力一样,只差等待
              次数;而出图后他看着结果决定,还能顺手改文案、改画风、用重画 chip。
              同一段话的 4 个随机种子,如果那段话本身有问题(1:1 糊字那种),4 张会一起错。
            ★组图 / 套图态的张数本来就是派生量(跟爆点数、渠道数走),摘要 chip 里写着,
              这里更不需要一个只读框占位。
            ★纯前端改动:`requestedCount` 契约不动,MULTI 态恒 1 张。 */}

        {/* 分辨率 */}
        <div>
          <SectionLabel>{t("freeCreate.tierLabel")}</SectionLabel>
          <div className="flex flex-wrap gap-1.5">
            {RESOLUTION_TIERS.filter(
              (tier) =>
                supportedTiers.includes(tier.value) && (!only1KReason || tier.value === "1K")
            ).map((tier) => {
              const legal = isCombinationLegal(validRatio, tier.value);
              const active = validTier === tier.value;
              const wh = legal && resolveSize ? resolveSize(validRatio, tier.value) : null;
              const title = !legal
                ? `${validRatio} + ${tier.value} 不支持`
                : wh
                  ? `${wh[0]}×${wh[1]}px (${((wh[0] * wh[1]) / 1_000_000).toFixed(1)}M)`
                  : undefined;
              return (
                <button
                  key={tier.value}
                  type="button"
                  disabled={!legal}
                  title={title}
                  onClick={() => legal && setResolutionTier(tier.value)}
                  className={cn(
                    "min-w-[60px] rounded-md border px-2.5 py-1 text-[13px] font-medium transition-colors",
                    !legal && "cursor-not-allowed opacity-40",
                    legal && "cursor-pointer",
                    legal && active
                      ? "border-primary bg-primary/10 text-primary"
                      : legal &&
                          "border-border/60 bg-background/40 text-muted-foreground hover:border-primary/40 hover:text-foreground"
                  )}
                  aria-pressed={active}
                >
                  {tier.label}
                  {/* 2026-07-30 —— 每档标**真实积分**(creditRates 单一真值源)。
                      ★1K 280 / 2K 700 / 4K 1400 —— 2K→4K 是翻倍,这种价差不能只写
                        「更清晰」了事:卡面假单价与真实费率差 16~69 倍那笔账就是这么来的,
                        而且一律往少了说。数字从 `creditRates` 算,不在这里硬编。 */}
                  <span
                    className={cn(
                      "ml-1 font-mono text-2xs font-normal",
                      legal && active ? "text-primary/80" : "text-muted-foreground"
                    )}
                  >
                    {creditsForTier(priceBook, provider, tier.value)}
                  </span>
                </button>
              );
            })}
          </div>
          {/* 锁 1K 时只说原因那一行:请求规格的像素(如 1056×1408)和实交约 1.6M 并排会自相矛盾 */}
          {validTier &&
            !only1KReason &&
            (() => {
              const wh = resolveSize?.(validRatio, validTier);
              const hint = wh
                ? `${wh[0]}×${wh[1]}px · ${((wh[0] * wh[1]) / 1_000_000).toFixed(1)}M`
                : RESOLUTION_TIERS.find((tier) => tier.value === validTier)?.hint;
              return hint ? (
                <>
                  <p className="mt-1 text-2xs text-muted-foreground">{hint}</p>
                  {/* 1005 生产验收:GPT 线路 2K 单交了 1086×1448。像素写的是请求规格,
                      上游不保证照给 —— 说清楚,并告诉他成品页会标实交像素。 */}
                  {provider.startsWith("OPENAI") && validTier !== "1K" && (
                    <p className="mt-0.5 text-2xs text-amber-600 dark:text-amber-500">
                      {OPENAI_SIZE_NOT_GUARANTEED_HINT}
                    </p>
                  )}
                </>
              ) : null;
            })()}
          {only1KReason && <p className="mt-1 text-2xs text-muted-foreground">{only1KReason}</p>}
        </div>

        <Separator />

        {/* 高级:模型 — 默认折叠,渐进式披露 */}
        <div>
          <button
            type="button"
            onClick={() => setShowAdvanced((v) => !v)}
            className="flex w-full items-center justify-between text-[12.5px] font-medium text-muted-foreground hover:text-foreground cursor-pointer"
            aria-expanded={showAdvanced}
          >
            <span>{t("freeCreate.advancedModel")}</span>
            <span className="text-2xs">
              {enabledProviders.find((p) => p.value === provider)?.label ?? provider}
              {showAdvanced ? " ▲" : " ▼"}
            </span>
          </button>
          {showAdvanced && (
            <div className="mt-2 space-y-2">
              <div className="grid grid-cols-2 gap-1.5">
                {enabledProviders.map((p) => {
                  const active = provider === p.value;
                  return (
                    <button
                      key={p.value}
                      type="button"
                      onClick={() => setProvider(p.value)}
                      className={cn(
                        "rounded-md border p-2 text-left transition-colors cursor-pointer",
                        active
                          ? "border-primary/60 bg-primary/10"
                          : "border-border/60 bg-background/40 hover:border-primary/30"
                      )}
                      aria-pressed={active}
                    >
                      <div className={cn("text-[13px] font-semibold", active && "text-primary")}>
                        {p.label}
                      </div>
                      <div className="text-2xs text-muted-foreground">{p.desc}</div>
                    </button>
                  );
                })}
              </div>
              {/* 1011 domesticOnly:深度构思走境外 responses,整条隐藏 */}
              {provider === "OPENAI" && !domesticOnly && (
                <div className="flex items-center justify-between gap-3 rounded-md border border-amber-500/40 bg-amber-500/5 p-2">
                  <Label
                    htmlFor="gpt-thinking-switch-params"
                    className="flex-1 cursor-pointer text-[12.5px] font-medium"
                  >
                    {t("freeCreate.gptThinking")}
                    <span className="mt-0.5 block text-2xs font-normal text-muted-foreground">
                      {t("freeCreate.gptThinkingHint")}
                    </span>
                  </Label>
                  <Switch
                    id="gpt-thinking-switch-params"
                    checked={useGptThinking}
                    onCheckedChange={setUseGptThinking}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
